// Sistema de traducciones para la aplicación de peritación 3D
import React from 'react';

// Definición del tipo para las traducciones
export type TranslationKey = keyof typeof translations.en;

// Idiomas soportados
export type Language = 'es' | 'fr' | 'en';

// Idioma actual (por defecto inglés)
let currentLanguage: Language = 'en';

// Intentar cargar el idioma guardado en localStorage, si existe
try {
    const savedLanguage = localStorage.getItem('vysia-language');
    if (savedLanguage && ['es', 'fr', 'en'].includes(savedLanguage)) {
        currentLanguage = savedLanguage as Language;
    } else {
        // Intentar detectar el idioma del navegador
        const browserLang = navigator.language.split('-')[0];
        if (['es', 'fr', 'en'].includes(browserLang)) {
            currentLanguage = browserLang as Language;
        }
    }
} catch (e) {
    console.warn('Error al cargar el idioma guardado', e);
}

// Diccionario de traducciones
export const translations = {
    en: {
        // Menú principal
        'app.title': 'VYSIA MODELVIEWER',
        'app.loading': 'Loading... Thanks for your patience',
        'app.takeScreenshot': 'Take screenshot',
        'app.initialButton': 'LOAD MODEL FROM URL',
        'app.dragDrop': 'Drag and drop .glb, .gltf or .ply files here, or click to open files',
        'app.dragDropMobile': 'Click to open files',
        'app.or': 'OR',
        'app.tipURL': 'Enter the URL of a .glb, .gltf or .ply file',
        'app.preparingFiles': 'Preparing files...',
        'app.processingModel': 'Processing model...',
        'app.loadingTextures': 'Loading textures...',
        'app.optimizing': 'Optimizing...',
        'app.loadingModels': 'Loading models...',
        'app.configuringScene': 'Configuring scene...',
        'app.finishingLoad': 'Finishing...',
        'app.finishedLoad': 'Load complete',

        // Botones de posición
        'position.front': 'Front',
        'position.frontRight': 'Front-Right',
        'position.back': 'Back',
        'position.backLeft': 'Back-Left',
        'position.left': 'Left',
        'position.leftFront': 'Left-Front',
        'position.right': 'Right',
        'position.rightBack': 'Right-Back',
        'position.top': 'Top',
        'position.topView': 'TOP VIEW',
        'position.zoom': 'Zoom',
        'position.lift': 'Lift',
        'position.panHorizontal': 'Pan Horizontal',

        // Paneles
        'panel.camera': 'Camera',
        'panel.skybox': 'Environment',
        'panel.light': 'Lighting',
        'panel.debug': 'Debug',
        'panel.view': 'View',
        'panel.scene': 'SCENE',
        'panel.hierarchy': 'HIERARCHY',
        'panel.device': 'DEVICE',

        // Scene panel
        'scene.filename': 'Filename',
        'scene.meshes': 'Meshes',
        'scene.materials': 'Materials',
        'scene.textures': 'Textures',
        'scene.primitives': 'Primitives',
        'scene.verts': 'Verts',
        'scene.meshVRAM': 'Mesh VRAM',
        'scene.textureVRAM': 'Texture VRAM',
        'scene.loadTime': 'Load time',
        'scene.bounds': 'Bounds',
        'scene.variant': 'Variant',

        // Opciones de cámara
        'camera.title': 'Camera',
        'camera.position': 'Position',
        'camera.fov': 'Field of View',
        'camera.tonemapping': 'Tone Mapping',
        'camera.pixelScale': 'Pixel Scale',
        'camera.multisample': 'Anti-aliasing',
        'camera.hq': 'High Quality',
        'camera.quality': 'Quality',

        // Opciones de entorno
        'skybox.name': 'Sky',
        'skybox.value': 'Environment',
        'skybox.exposure': 'Exposure',
        'skybox.rotation': 'Rotation',
        'skybox.background': 'Background Type',
        'skybox.backgroundColor': 'Background Color',
        'skybox.blur': 'Blur',
        'skybox.scale': 'Scale',
        'skybox.tripodOffset': 'Tripod Offset',

        //Share options
        'share.titleQR': 'View and share on mobile with QR code',
        'share.titleURL': 'View and share on mobile with URL',

        // Opciones de luz
        'light.title': 'Light',
        'light.enabled': 'Enable Light',
        'light.intensity': 'Intensity',
        'light.color': 'Color',
        'light.follow': 'Follow Camera',
        'light.shadow': 'Cast Shadows',
        'light.shadowCatcher': 'Shadow Catcher',
        'light.catcherIntensity': 'Catcher Intensity',

        // Opciones de debug
        'debug.title': 'Debug',
        'debug.renderMode': 'Render Mode',
        'debug.stats': 'Show Stats',
        'debug.wireframe': 'Wireframe',
        'debug.bounds': 'Bounding Box',
        'debug.skeleton': 'Skeleton',
        'debug.axes': 'Axes',
        'debug.grid': 'Grid',
        'debug.normals': 'Normals',

        // Animaciones
        'animation.playing': 'Playing',
        'animation.speed': 'Speed',
        'animation.transition': 'Transition',
        'animation.loops': 'Loops',

        // Errores
        'error.generic': 'An error occurred',
        'error.load': 'Error loading file',
        'error.unsupported': 'Unsupported file format',
        'error.screenshot': 'Error taking screenshot',
        'error.noViewer': 'Viewer not available',
        'error.saveDamages': 'Error saving damages',

        // API messages
        'api.savingDamages': 'Saving damages...',
        'api.damagesSaved': 'Damages saved successfully to server',
        'api.errorSavingDamages': 'Error saving damages to server',
        'api.updatingDamages': 'Updating damages...',
        'api.damagesUpdated': 'Damages updated successfully',
        'api.errorUpdatingDamages': 'Error updating damages',
        'api.loadingDamages': 'Loading damages from server...',
        'api.damagesLoaded': 'Damages loaded successfully from server',
        'api.errorLoadingDamages': 'Error loading damages from server',
        'api.noDamagesFound': 'No damages found for this request',
        'api.noRequestId': 'Request ID not available',
        'api.damagesCount': 'damage(s) loaded',

        // AR/VR
        'xr.enter': 'Enter AR mode',
        'xr.exit': 'Exit AR mode',

        // Otros
        'action.fullscreen': 'Toggle fullscreen',
        'action.resetView': 'Reset view',
        'action.centerModel': 'Center model',
        'language': 'Language',

        // Device panel
        'device.webgpu': 'Use WebGPU',
        'device.activeDevice': 'Active Device',
        'device.viewport': 'Viewport',

        // Markers/Spheres - Position markers
        'markers.selectDamage': 'Select damage',
        'markers.selectSphere': 'Select a damage',
        'markers.refreshList': 'Refresh list',
        'markers.sphereName': 'Damage Name',
        'markers.sphereDescription': 'Damage Description',
        'markers.selectSide': 'Select Side',
        'markers.sideFront': 'Front',
        'markers.sideLeft': 'Left',
        'markers.sideRight': 'Right',
        'markers.sideBack': 'Back',
        'markers.create': 'Create',
        'markers.list': 'List damages',
        'markers.hide': 'Hide',
        'markers.edit': 'Edit',
        'markers.export': 'Save',
        'markers.import': 'Import',
        'markers.accept': 'Accept',
        'markers.confirm': 'Confirm',
        'markers.cancel': 'Cancel',
        'markers.delete': 'Delete',
        'markers.createdSuccess': 'Damage created successfully!',
        'markers.fillAllFields': 'Please fill in all fields.',
        'markers.selectSphereEdit': 'Select a damage to edit',
        'markers.editingMode': 'You are now editing mode. Select a damage to edit.',
        'markers.createMessage': 'Place the sphere where is the damage, to move use the color arrows. Make sure that the sphere is visible.',
        'markers.legacyReadOnly': 'Legacy manual markers are read-only. Create and edit damages from a camera photo.',
        'markers.tableName': 'Name',
        'markers.tableDescription': 'Description',
        'markers.tableSide': 'Side',

        // Image Gallery
        'gallery.title': 'Photo Gallery',
        'gallery.loading': 'Loading photos...',
        'gallery.loadError': 'Error loading photos',
        'gallery.noPhotos': 'No photos available',
        'gallery.noRequestId': 'Request ID not found',
        'gallery.close': 'Close',
        'gallery.download': 'Download',
        'gallery.expand': 'Expand image',
        'gallery.downloadError': 'Could not download the image locally',
        'gallery.zoom': 'Zoom',
        'gallery.markerMode': 'Add marker',
        'gallery.markerConfirm': 'Confirm marker',
        'gallery.markerCancel': 'Cancel marker',
        'gallery.markerEdit': 'Edit marker',
        'gallery.markerDelete': 'Delete marker',
        'gallery.previous': 'Previous',
        'gallery.next': 'Next',
        'gallery.photo': 'Photo',
        'gallery.of': 'of',
        'gallery.options': 'Options',
        'gallery.selectForAssignment': 'Select for assignment',
        'gallery.assignToDamage': 'Assign to damage',
        'gallery.noDamages': 'No damages created yet',
        'gallery.selectedCount': '{count} photo(s) selected',
        'gallery.openGallery': 'Open Photo Gallery',
        'gallery.cancel': 'Cancel',
        'gallery.add': 'Add',
        'gallery.selectPhotos': 'Select Photos',
        'gallery.damagePhotos': 'Damage Photos',
        'gallery.viewPhotos': 'View Photos',
        'gallery.selectMode': 'Select',
        'gallery.enterSelectionMode': 'Activate selection',
        'gallery.exitSelectionMode': 'Exit selection',
        'gallery.imageUnavailable': 'Image unavailable',
        'cameraPositions.toggle': 'Camera Positions',

        // UI Panel keys
        'ui.gearTooltip': 'Settings',
        'ui.positions': 'Positions',
        'ui.augmentedNavMap': 'Augmented navigation map',
        'ui.damageToggle': 'Damages',
        'cameraPositions.associateDamage': 'Associate To Damage',
        'cameraPositions.useExistingDamage': 'Use Existing Damage',
        'cameraPositions.createNewDamage': 'Create New Damage',
        'cameraPositions.deleteConfirmMessage': 'Are you sure you want to delete this damage?',
        'cameraPositions.viewNotes': 'View Notes',
        'cameraPositions.noNotes': 'No notes available yet',
        'cameraPositions.newNote': 'New note',
        'cameraPositions.editSelectedNote': 'Edit selected',
        'cameraPositions.deleteSelectedNote': 'Delete selected',
        'cameraPositions.exitNotes': 'Exit',
        'cameraPositions.noteDeleteConfirmMessage': 'Are you sure you want to delete this note?',
        'cameraPositions.noteAuthor': 'Who does it',
        'cameraPositions.noteMessage': 'What it says',
        'cameraPositions.markerNoDamagePrompt': 'This camera has no associated damage. Do you want to create one now?',
        'cameraPositions.markerSelectDamagePrompt': 'Select or associate a damage before creating a marker. Open association now?',
        'cameraPositions.markerGlobalNoDamagePrompt': 'No damages exist yet. Create a new damage to associate this mark?',
        'cameraPositions.markerGlobalSelectDamagePrompt': 'Associate this mark to an existing damage or create a new one.',
        'camera.orbitalMode': 'Orbital Mode',

        // Damage UX
        'damage.sheetTitle': 'Damage Sheet',
        'damage.addDamage': 'Add damage',
        'damage.createWithMark': 'Create damage with mark',
        'damage.toggleMenu': 'Show damages',
        'damage.confirmMark': 'Confirm mark',
        'damage.cancelMark': 'Cancel mark',
        'damage.legendSelected': 'Selected damage',
        'damage.legendUnselected': 'Other damages',

        // Fallback Gallery
        'fallbackGallery.title': 'Photo Navigation',
        'fallbackGallery.noModelPopup': 'The 3D model was not generated correctly, therefore only the photo navigation system will be available.',
        'fallbackGallery.noModelPopupClose': 'Close',
        'fallbackGallery.loading': 'Loading images...',
        'fallbackGallery.noPhotos': 'No photos available',
        'fallbackGallery.loadError': 'Error loading images',
        'fallbackGallery.saveError': 'Error saving damages',
        'fallbackGallery.addDamage': 'Add damage',
        'fallbackGallery.saveDamages': 'Save damages',
        'fallbackGallery.saving': 'Saving...',
        'fallbackGallery.saved': 'Saved successfully',
        'fallbackGallery.cancel': 'Cancel',
        'fallbackGallery.confirm': 'Confirm',
        'fallbackGallery.delete': 'Delete',
        'fallbackGallery.damageName': 'Damage name',
        'fallbackGallery.damageDescription': 'Damage description',
        'fallbackGallery.damageSide': 'Damage side',
        'fallbackGallery.close': 'Close',
        'fallbackGallery.previous': 'Previous',
        'fallbackGallery.next': 'Next',
        'fallbackGallery.photo': 'Photo',
        'fallbackGallery.of': 'of',
        'fallbackGallery.retry': 'Retry',
        'fallbackGallery.editDamage': 'Edit damage',
        'fallbackGallery.deleteDamage': 'Delete damage',
        'fallbackGallery.confirmDelete': 'Are you sure you want to delete this damage?'
    },

    es: {
        // Menú principal
        'app.title': 'VYSIA MODELVIEWER',
        'app.loading': 'Cargando... Gracias por su paciencia',
        'app.takeScreenshot': 'Capturar pantalla',

        // Carga de archivos
        'app.initialButton': 'CARGAR MODELO DESDE URL',
        'app.dragDrop': 'Arrastra y suelta archivos .glb, .gltf o .ply aquí, o haz clic para abrir archivos',
        'app.dragDropMobile': 'Haz clic para abrir archivos',
        'app.or': 'O',
        'app.tipURL': 'Introduce la URL de un archivo .glb, .gltf o .ply',

        // Proceso de carga
        'app.preparingFiles': 'Preparando archivos...',
        'app.processingModel': 'Procesando modelo...',
        'app.loadingTextures': 'Cargando texturas...',
        'app.optimizing': 'Optimizando...',
        'app.loadingModels': 'Cargando modelos...',
        'app.configuringScene': 'Configurando escena...',
        'app.finishingLoad': 'Finalizando...',
        'app.finishedLoad': 'Carga completada',

        // Botones de posición
        'position.front': 'Frontal',
        'position.frontRight': 'Frontal-Derecha',
        'position.back': 'Trasera',
        'position.backLeft': 'Trasera-Izquierda',
        'position.left': 'Izquierda',
        'position.leftFront': 'Izquierda-Frontal',
        'position.right': 'Derecha',
        'position.rightBack': 'Derecha-Trasera',
        'position.top': 'Superior',
        'position.topView': 'VISTA SUPERIOR',

        'position.zoom': 'Zoom',
        'position.lift': 'Elevar',
        'position.panHorizontal': 'Desplazar Horizontal',

        // Paneles
        'panel.camera': 'Cámara',
        'panel.skybox': 'Entorno',
        'panel.light': 'Iluminación',
        'panel.debug': 'Depuración',
        'panel.view': 'Vista',
        'panel.scene': 'ESCENA',
        'panel.hierarchy': 'JERARQUÍA',
        'panel.device': 'DISPOSITIVO',

        // Scene panel
        'scene.filename': 'Archivo',
        'scene.meshes': 'Mallas',
        'scene.materials': 'Materiales',
        'scene.textures': 'Texturas',
        'scene.primitives': 'Primitivas',
        'scene.verts': 'Vértices',
        'scene.meshVRAM': 'VRAM de malla',
        'scene.textureVRAM': 'VRAM de textura',
        'scene.loadTime': 'Tiempo de carga',
        'scene.bounds': 'Límites',
        'scene.variant': 'Variante',

        // Opciones de cámara
        'camera.title': 'Cámara',
        'camera.position': 'Posición',
        'camera.fov': 'Campo de visión',
        'camera.tonemapping': 'Mapeo tonal',
        'camera.pixelScale': 'Escala de píxeles',
        'camera.multisample': 'Antialiasing',
        'camera.hq': 'Alta calidad',
        'camera.quality': 'Calidad',

        // Opciones de entorno
        'skybox.name': 'Cielo',
        'skybox.value': 'Entorno',
        'skybox.exposure': 'Exposición',
        'skybox.rotation': 'Rotación',
        'skybox.background': 'Tipo de fondo',
        'skybox.backgroundColor': 'Color de fondo',
        'skybox.blur': 'Desenfoque',
        'skybox.scale': 'Escala',
        'skybox.tripodOffset': 'Desplazamiento trípode',

        //Share options
        'share.titleQR': 'Ver y compartir en móvil con código QR',
        'share.titleURL': 'Ver y compartir en móvil con URL',

        // Opciones de luz
        'light.title': 'Iluminación',
        'light.enabled': 'Activar luz',
        'light.intensity': 'Intensidad',
        'light.color': 'Color',
        'light.follow': 'Seguir cámara',
        'light.shadow': 'Proyectar sombras',
        'light.shadowCatcher': 'Capturador de sombras',
        'light.catcherIntensity': 'Intensidad del capturador',

        // Opciones de debug
        'debug.title': 'Depuración',
        'debug.renderMode': 'Modo de renderizado',
        'debug.stats': 'Mostrar estadísticas',
        'debug.wireframe': 'Wireframe',
        'debug.bounds': 'Caja delimitadora',
        'debug.skeleton': 'Esqueleto',
        'debug.axes': 'Ejes',
        'debug.grid': 'Cuadrícula',
        'debug.normals': 'Normales',

        // Animaciones
        'animation.playing': 'Reproduciendo',
        'animation.speed': 'Velocidad',
        'animation.transition': 'Transición',
        'animation.loops': 'Bucles',


        // Errores
        'error.generic': 'Ha ocurrido un error',
        'error.load': 'Error al cargar el archivo',
        'error.unsupported': 'Formato de archivo no soportado',
        'error.screenshot': 'Error al tomar la captura de pantalla',
        'error.noViewer': 'El visor no está disponible',
        'error.saveDamages': 'Error al guardar los daños',

        // API messages
        'api.savingDamages': 'Guardando daños...',
        'api.damagesSaved': 'Daños guardados correctamente en el servidor',
        'api.errorSavingDamages': 'Error al guardar los daños en el servidor',
        'api.updatingDamages': 'Actualizando daños...',
        'api.damagesUpdated': 'Daños actualizados correctamente',
        'api.errorUpdatingDamages': 'Error al actualizar los daños',
        'api.loadingDamages': 'Cargando daños desde el servidor...',
        'api.damagesLoaded': 'Daños cargados correctamente desde el servidor',
        'api.errorLoadingDamages': 'Error al cargar los daños desde el servidor',
        'api.noDamagesFound': 'No se encontraron daños para esta solicitud',
        'api.noRequestId': 'ID de solicitud no disponible',
        'api.damagesCount': 'daño(s) cargado(s)',

        // AR/VR
        'xr.enter': 'Entrar en modo AR',
        'xr.exit': 'Salir del modo AR',

        // Otros
        'action.fullscreen': 'Alternar pantalla completa',
        'action.resetView': 'Restablecer vista',
        'action.centerModel': 'Centrar modelo',
        'language': 'Idioma',

        // Device panel
        'device.webgpu': 'Usar WebGPU',
        'device.activeDevice': 'Dispositivo activo',
        'device.viewport': 'Vista',

        // Markers/Spheres - Marcadores de posición
        'markers.selectDamage': 'Seleccionar daño',
        'markers.selectSphere': 'Selecciona un daño',
        'markers.refreshList': 'Actualizar lista',
        'markers.sphereName': 'Nombre del daño',
        'markers.sphereDescription': 'Descripción del daño',
        'markers.selectSide': 'Seleccionar lado',
        'markers.sideFront': 'Frontal',
        'markers.sideLeft': 'Izquierda',
        'markers.sideRight': 'Derecha',
        'markers.sideBack': 'Trasera',
        'markers.create': 'Crear',
        'markers.list': 'Listar daños',
        'markers.hide': 'Ocultar',
        'markers.edit': 'Editar',
        'markers.export': 'Guardar',
        'markers.import': 'Importar',
        'markers.accept': 'Aceptar',
        'markers.confirm': 'Confirmar',
        'markers.cancel': 'Cancelar',
        'markers.delete': 'Eliminar',
        'markers.createdSuccess': '¡Daño creado exitosamente!',
        'markers.fillAllFields': 'Por favor, completa todos los campos.',
        'markers.selectSphereEdit': 'Selecciona un daño para editar',
        'markers.editingMode': 'Ahora estás en modo de edición. Selecciona un daño para editar.',
        'markers.createMessage': 'Coloca la esfera donde está el daño, para moverla usa las flechas de color. Asegúrate de que la esfera sea visible.',
        'markers.legacyReadOnly': 'Los marcadores manuales legacy son de solo lectura. Crea y edita daños desde una foto de cámara.',
        'markers.tableName': 'Nombre',
        'markers.tableDescription': 'Descripción',
        'markers.tableSide': 'Lado',

        // Galería de imágenes
        'gallery.title': 'Galería de Fotos',
        'gallery.loading': 'Cargando fotos...',
        'gallery.loadError': 'Error al cargar las fotos',
        'gallery.noPhotos': 'No hay fotos disponibles',
        'gallery.noRequestId': 'ID de solicitud no encontrado',
        'gallery.close': 'Cerrar',
        'gallery.download': 'Descargar',
        'gallery.expand': 'Ampliar imagen',
        'gallery.downloadError': 'No se pudo descargar la imagen en local',
        'gallery.zoom': 'Zoom',
        'gallery.markerMode': 'Anadir marca',
        'gallery.markerConfirm': 'Confirmar marca',
        'gallery.markerCancel': 'Cancelar marca',
        'gallery.markerEdit': 'Editar marca',
        'gallery.markerDelete': 'Eliminar marca',
        'gallery.previous': 'Anterior',
        'gallery.next': 'Siguiente',
        'gallery.photo': 'Foto',
        'gallery.of': 'de',
        'gallery.options': 'Opciones',
        'gallery.selectForAssignment': 'Seleccionar para asignar',
        'gallery.assignToDamage': 'Asignar a daño',
        'gallery.noDamages': 'No hay daños creados aún',
        'gallery.selectedCount': '{count} foto(s) seleccionada(s)',
        'gallery.openGallery': 'Abrir Galería de Fotos',
        'gallery.cancel': 'Cancelar',
        'gallery.add': 'Agregar',
        'gallery.selectPhotos': 'Seleccionar Fotos',
        'gallery.damagePhotos': 'Fotos del Daño',
        'gallery.viewPhotos': 'Ver Fotos',
        'gallery.selectMode': 'Seleccionar',
        'gallery.enterSelectionMode': 'Activar selección',
        'gallery.exitSelectionMode': 'Salir de selección',
        'gallery.imageUnavailable': 'Imagen no disponible',
        'cameraPositions.toggle': 'Posiciones de Cámara',

        // UI Panel keys
        'ui.gearTooltip': 'Configuración',
        'ui.positions': 'Posiciones',
        'ui.augmentedNavMap': 'Mapa de navegación aumentada',
        'ui.damageToggle': 'Daños',
        'cameraPositions.associateDamage': 'Asociar a daño',
        'cameraPositions.useExistingDamage': 'Usar daño existente',
        'cameraPositions.createNewDamage': 'Crear daño nuevo',
        'cameraPositions.deleteConfirmMessage': '¿Seguro que deseas eliminar este daño?',
        'cameraPositions.viewNotes': 'Ver notas',
        'cameraPositions.noNotes': 'No hay notas aún',
        'cameraPositions.newNote': 'Nueva nota',
        'cameraPositions.editSelectedNote': 'Editar seleccionada',
        'cameraPositions.deleteSelectedNote': 'Borrar seleccionada',
        'cameraPositions.exitNotes': 'Salir',
        'cameraPositions.noteDeleteConfirmMessage': '¿Seguro que deseas eliminar esta nota?',
        'cameraPositions.noteAuthor': 'Quién lo hace',
        'cameraPositions.noteMessage': 'Qué dice',
        'cameraPositions.markerNoDamagePrompt': 'Esta cámara no tiene daños asociados. ¿Deseas crear uno ahora?',
        'cameraPositions.markerSelectDamagePrompt': 'Selecciona o asocia un daño antes de crear una marca. ¿Abrir asociación ahora?',
        'cameraPositions.markerGlobalNoDamagePrompt': 'No existen daños aún. ¿Crear un nuevo daño para asociar esta marca?',
        'cameraPositions.markerGlobalSelectDamagePrompt': 'Asocia esta marca a un daño existente o crea uno nuevo.',
        'camera.orbitalMode': 'Modo orbital',

        // Damage UX
        'damage.sheetTitle': 'Ficha del daño',
        'damage.addDamage': 'Añadir daño',
        'damage.createWithMark': 'Crear daño con marca',
        'damage.toggleMenu': 'Mostrar daños',
        'damage.confirmMark': 'Confirmar marca',
        'damage.cancelMark': 'Cancelar marca',
        'damage.legendSelected': 'Daño seleccionado',
        'damage.legendUnselected': 'Otros daños',

        // Galería de Fallback
        'fallbackGallery.title': 'Navegación por fotos',
        'fallbackGallery.noModelPopup': 'No se ha generado correctamente el modelo 3D, por lo tanto solo estará disponible el sistema de navegación por fotos.',
        'fallbackGallery.noModelPopupClose': 'Cerrar',
        'fallbackGallery.loading': 'Cargando imágenes...',
        'fallbackGallery.noPhotos': 'No hay fotos disponibles',
        'fallbackGallery.loadError': 'Error al cargar las imágenes',
        'fallbackGallery.saveError': 'Error al guardar los daños',
        'fallbackGallery.addDamage': 'Añadir daño',
        'fallbackGallery.saveDamages': 'Guardar daños',
        'fallbackGallery.saving': 'Guardando...',
        'fallbackGallery.saved': 'Guardado correctamente',
        'fallbackGallery.cancel': 'Cancelar',
        'fallbackGallery.confirm': 'Confirmar',
        'fallbackGallery.delete': 'Eliminar',
        'fallbackGallery.damageName': 'Nombre del daño',
        'fallbackGallery.damageDescription': 'Descripción del daño',
        'fallbackGallery.damageSide': 'Lado del daño',
        'fallbackGallery.close': 'Cerrar',
        'fallbackGallery.previous': 'Anterior',
        'fallbackGallery.next': 'Siguiente',
        'fallbackGallery.photo': 'Foto',
        'fallbackGallery.of': 'de',
        'fallbackGallery.retry': 'Reintentar',
        'fallbackGallery.editDamage': 'Editar daño',
        'fallbackGallery.deleteDamage': 'Eliminar daño',
        'fallbackGallery.confirmDelete': '¿Seguro que deseas eliminar este daño?'
    },

    fr: {
        // Menu principal
        'app.title': 'VYSIA MODELVIEWER',
        'app.loading': 'Chargement... Merci pour votre patience',
        'app.takeScreenshot': 'Capture d\'écran',
        'app.initialButton': 'CHARGER LE MODÈLE DEPUIS URL',
        'app.dragDrop': 'Glissez-déposez des fichiers .glb, .gltf ou .ply ici, ou cliquez pour ouvrir des fichiers',
        'app.dragDropMobile': 'Cliquez pour ouvrir des fichiers',
        'app.or': 'OU',
        'app.tipURL': 'Entrez l\'URL d\'un fichier .glb, .gltf ou .ply',
        'app.preparingFiles': 'Préparation des fichiers...',
        'app.processingModel': 'Traitement du modèle...',
        'app.loadingTextures': 'Chargement des textures...',
        'app.optimizing': 'Optimisation...',
        'app.loadingModels': 'Chargement des modèles...',
        'app.configuringScene': 'Configuration de la scène...',
        'app.finishingLoad': 'Finalisation...',
        'app.finishedLoad': 'Chargement terminé',

        // Botones de posición
        'position.front': 'Avant',
        'position.frontRight': 'Avant-Droite',
        'position.back': 'Arrière',
        'position.backLeft': 'Arrière-Gauche',
        'position.left': 'Gauche',
        'position.leftFront': 'Gauche-Avant',
        'position.right': 'Droite',
        'position.rightBack': 'Droite-Arrière',
        'position.top': 'Dessus',
        'position.topView': 'VUE DE DESSUS',

        'position.zoom': 'Zoom',
        'position.lift': 'Élever',
        'position.panHorizontal': 'Déplacer Horizontalement',

        // Panneaux
        'panel.camera': 'Caméra',
        'panel.skybox': 'Environnement',
        'panel.light': 'Éclairage',
        'panel.debug': 'Débogage',
        'panel.view': 'Vue',
        'panel.scene': 'SCÈNE',
        'panel.hierarchy': 'HIÉRARCHIE',
        'panel.device': 'APPAREIL',

        // Scene panel
        'scene.filename': 'Nom de fichier',
        'scene.meshes': 'Maillages',
        'scene.materials': 'Matériaux',
        'scene.textures': 'Textures',
        'scene.primitives': 'Primitives',
        'scene.verts': 'Sommets',
        'scene.meshVRAM': 'VRAM de maillage',
        'scene.textureVRAM': 'VRAM de texture',
        'scene.loadTime': 'Temps de chargement',
        'scene.bounds': 'Limites',
        'scene.variant': 'Variante',

        // Options de caméra
        'camera.title': 'Caméra',
        'camera.position': 'Position',
        'camera.fov': 'Champ de vision',
        'camera.tonemapping': 'Mappage tonal',
        'camera.pixelScale': 'Échelle des pixels',
        'camera.multisample': 'Anti-crénelage',
        'camera.hq': 'Haute qualité',
        'camera.quality': 'Qualité',

        // Options d'environnement
        'skybox.name': 'Ciel',
        'skybox.value': 'Environnement',
        'skybox.exposure': 'Exposition',
        'skybox.rotation': 'Rotation',
        'skybox.background': 'Type d\'arrière-plan',
        'skybox.backgroundColor': 'Couleur d\'arrière-plan',
        'skybox.blur': 'Flou',
        'skybox.scale': 'Échelle',
        'skybox.tripodOffset': 'Déplacement du trépied',

        //Share options
        'share.titleQR': 'Voir et partager sur mobile avec le code QR',
        'share.titleURL': 'Voir et partager sur mobile avec l\'URL',

        // Options de lumière
        'light.title': 'Éclairage',
        'light.enabled': 'Activer la lumière',
        'light.intensity': 'Intensité',
        'light.color': 'Couleur',
        'light.follow': 'Suivre la caméra',
        'light.shadow': 'Projeter des ombres',
        'light.shadowCatcher': 'Capteur d\'ombres',
        'light.catcherIntensity': 'Intensité du capteur',

        // Options de débogage
        'debug.title': 'Débogage',
        'debug.renderMode': 'Mode de rendu',
        'debug.stats': 'Afficher les statistiques',
        'debug.wireframe': 'Fil de fer',
        'debug.bounds': 'Boîte englobante',
        'debug.skeleton': 'Squelette',
        'debug.axes': 'Axes',
        'debug.grid': 'Grille',
        'debug.normals': 'Normales',

        // Animations
        'animation.playing': 'Lecture',
        'animation.speed': 'Vitesse',
        'animation.transition': 'Transition',
        'animation.loops': 'Boucles',


        // Erreurs
        'error.generic': 'Une erreur est survenue',
        'error.load': 'Erreur lors du chargement du fichier',
        'error.unsupported': 'Format de fichier non pris en charge',
        'error.screenshot': 'Erreur lors de la capture d\'écran',
        'error.noViewer': 'Visualiseur non disponible',
        'error.saveDamages': 'Erreur lors de la sauvegarde des dommages',

        // API messages
        'api.savingDamages': 'Enregistrement des dommages...',
        'api.damagesSaved': 'Dommages enregistrés avec succès sur le serveur',
        'api.errorSavingDamages': 'Erreur lors de l\'enregistrement des dommages sur le serveur',
        'api.updatingDamages': 'Mise à jour des dommages...',
        'api.damagesUpdated': 'Dommages mis à jour avec succès',
        'api.errorUpdatingDamages': 'Erreur lors de la mise à jour des dommages',
        'api.loadingDamages': 'Chargement des dommages depuis le serveur...',
        'api.damagesLoaded': 'Dommages chargés avec succès depuis le serveur',
        'api.errorLoadingDamages': 'Erreur lors du chargement des dommages depuis le serveur',
        'api.noDamagesFound': 'Aucun dommage trouvé pour cette demande',
        'api.noRequestId': 'ID de demande non disponible',
        'api.damagesCount': 'dommage(s) chargé(s)',

        // RA/RV
        'xr.enter': 'Entrer en mode RA',
        'xr.exit': 'Quitter le mode RA',

        // Autres
        'action.fullscreen': 'Basculer en plein écran',
        'action.resetView': 'Réinitialiser la vue',
        'action.centerModel': 'Centrer le modèle',
        'language': 'Langue',

        // Device panel
        'device.webgpu': 'Utiliser WebGPU',
        'device.activeDevice': 'Dispositif actif',
        'device.viewport': 'Viewport',

        // Markers/Spheres - Marqueurs de position
        'markers.selectDamage': 'Sélectionner un dommage',
        'markers.selectSphere': 'Sélectionner une dommage',
        'markers.refreshList': 'Actualiser la liste',
        'markers.sphereName': 'Nom du dommage',
        'markers.sphereDescription': 'Description du dommage',
        'markers.selectSide': 'Sélectionner le côté',
        'markers.sideFront': 'Avant',
        'markers.sideLeft': 'Gauche',
        'markers.sideRight': 'Droite',
        'markers.sideBack': 'Arrière',
        'markers.create': 'Créer',
        'markers.list': 'Lister les dommages',
        'markers.hide': 'Masquer',
        'markers.edit': 'Modifier',
        'markers.export': 'Enregistrer',
        'markers.import': 'Importer',
        'markers.accept': 'Accepter',
        'markers.confirm': 'Confirmer',
        'markers.cancel': 'Annuler',
        'markers.delete': 'Supprimer',
        'markers.createdSuccess': 'Dommage créé avec succès!',
        'markers.fillAllFields': 'Veuillez remplir tous les champs.',
        'markers.selectSphereEdit': 'Sélectionnez un dommage à modifier',
        'markers.editingMode': 'Vous êtes maintenant en mode édition. Sélectionnez un dommage à modifier.',
        'markers.createMessage': 'Placez la sphère où se trouve le dommage, pour déplacer utilisez les flèches colorées. Assurez-vous que la sphère est visible.',
        'markers.legacyReadOnly': 'Les marqueurs manuels legacy sont en lecture seule. Créez et modifiez les dommages depuis une photo de caméra.',
        'markers.tableName': 'Nom',
        'markers.tableDescription': 'Description',
        'markers.tableSide': 'Côté',

        // Galeria de imágenes
        'gallery.title': 'Galerie de photos',
        'gallery.loading': 'Chargement des photos...',
        'gallery.loadError': 'Erreur lors du chargement des photos',
        'gallery.noPhotos': 'Aucune photo disponible',
        'gallery.noRequestId': 'ID de demande introuvable',
        'gallery.close': 'Fermer',
        'gallery.download': 'Télécharger',
        'gallery.expand': 'Agrandir image',
        'gallery.downloadError': 'Impossible de télécharger l\'image en local',
        'gallery.zoom': 'Zoom',
        'gallery.markerMode': 'Ajouter un marqueur',
        'gallery.markerConfirm': 'Confirmer marqueur',
        'gallery.markerCancel': 'Annuler marqueur',
        'gallery.markerEdit': 'Modifier marqueur',
        'gallery.markerDelete': 'Supprimer marqueur',
        'gallery.previous': 'Précédent',
        'gallery.next': 'Suivant',
        'gallery.photo': 'Photo',
        'gallery.of': 'de',
        'gallery.options': 'Options',
        'gallery.selectForAssignment': 'Sélectionner pour l\'attribution',
        'gallery.assignToDamage': 'Attribuer au dommage',
        'gallery.noDamages': 'Aucun dommage créé',
        'gallery.selectedCount': '{count} photo(s) sélectionnée(s)',
        'gallery.openGallery': 'Ouvrir la galerie de photos',
        'gallery.cancel': 'Annuler',
        'gallery.add': 'Ajouter',
        'gallery.selectPhotos': 'Sélectionner des photos',
        'gallery.damagePhotos': 'Photos du dommage',
        'gallery.viewPhotos': 'Voir les photos',
        'gallery.selectMode': 'Sélectionner',
        'gallery.enterSelectionMode': 'Activer la sélection',
        'gallery.exitSelectionMode': 'Quitter la sélection',
        'gallery.imageUnavailable': 'Image non disponible',
        'cameraPositions.toggle': 'Positions des caméras',

        // UI Panel keys
        'ui.gearTooltip': 'Paramètres',
        'ui.positions': 'Positions',
        'ui.augmentedNavMap': 'Carte de navigation augmentée',
        'ui.damageToggle': 'Dommages',
        'cameraPositions.associateDamage': 'Associer au dommage',
        'cameraPositions.useExistingDamage': 'Utiliser un dommage existant',
        'cameraPositions.createNewDamage': 'Créer un nouveau dommage',
        'cameraPositions.deleteConfirmMessage': 'Voulez-vous vraiment supprimer ce dommage ?',
        'cameraPositions.viewNotes': 'Voir les notes',
        'cameraPositions.noNotes': 'Aucune note pour le moment',
        'cameraPositions.newNote': 'Nouvelle note',
        'cameraPositions.editSelectedNote': 'Modifier la sélection',
        'cameraPositions.deleteSelectedNote': 'Supprimer la sélection',
        'cameraPositions.exitNotes': 'Sortir',
        'cameraPositions.noteDeleteConfirmMessage': 'Voulez-vous vraiment supprimer cette note ?',
        'cameraPositions.noteAuthor': 'Qui le fait',
        'cameraPositions.noteMessage': 'Ce qui est dit',
        'cameraPositions.markerNoDamagePrompt': 'Cette caméra n\'a aucun dommage associé. Voulez-vous en créer un maintenant ?',
        'cameraPositions.markerSelectDamagePrompt': 'Sélectionnez ou associez un dommage avant de créer un marqueur. Ouvrir l\'association maintenant ?',
        'cameraPositions.markerGlobalNoDamagePrompt': 'Aucun dommage n\'existe encore. Créer un nouveau dommage pour associer cette marque ?',
        'cameraPositions.markerGlobalSelectDamagePrompt': 'Associez cette marque à un dommage existant ou créez-en un nouveau.',
        'camera.orbitalMode': 'Mode orbital',

        // Damage UX
        'damage.sheetTitle': 'Fiche du dommage',
        'damage.addDamage': 'Ajouter un dommage',
        'damage.createWithMark': 'Créer un dommage avec marque',
        'damage.toggleMenu': 'Afficher dommages',
        'damage.confirmMark': 'Confirmer le marqueur',
        'damage.cancelMark': 'Annuler le marqueur',
        'damage.legendSelected': 'Dommage selectionne',
        'damage.legendUnselected': 'Autres dommages',

        // Galerie de Fallback
        'fallbackGallery.title': 'Navigation par photos',
        'fallbackGallery.noModelPopup': 'Le modèle 3D n\'a pas été généré correctement, par conséquent seul le système de navigation par photos sera disponible.',
        'fallbackGallery.noModelPopupClose': 'Fermer',
        'fallbackGallery.loading': 'Chargement des images...',
        'fallbackGallery.noPhotos': 'Aucune photo disponible',
        'fallbackGallery.loadError': 'Erreur lors du chargement des images',
        'fallbackGallery.saveError': 'Erreur lors de la sauvegarde des dommages',
        'fallbackGallery.addDamage': 'Ajouter un dommage',
        'fallbackGallery.saveDamages': 'Enregistrer les dommages',
        'fallbackGallery.saving': 'Enregistrement...',
        'fallbackGallery.saved': 'Enregistré avec succès',
        'fallbackGallery.cancel': 'Annuler',
        'fallbackGallery.confirm': 'Confirmer',
        'fallbackGallery.delete': 'Supprimer',
        'fallbackGallery.damageName': 'Nom du dommage',
        'fallbackGallery.damageDescription': 'Description du dommage',
        'fallbackGallery.damageSide': 'Côté du dommage',
        'fallbackGallery.close': 'Fermer',
        'fallbackGallery.previous': 'Précédent',
        'fallbackGallery.next': 'Suivant',
        'fallbackGallery.photo': 'Photo',
        'fallbackGallery.of': 'de',
        'fallbackGallery.retry': 'Réessayer',
        'fallbackGallery.editDamage': 'Modifier le dommage',
        'fallbackGallery.deleteDamage': 'Supprimer le dommage',
        'fallbackGallery.confirmDelete': 'Voulez-vous vraiment supprimer ce dommage ?'
    }
};

/**
 * Obtiene la traducción de una clave en el idioma actual
 * @param key - Clave de traducción
 * @param params - Parámetros opcionales para reemplazar en el texto
 * @returns Texto traducido
 */
export function t(key: TranslationKey, ...params: string[]): string {
    const lang = currentLanguage;
    let text = translations[lang][key] || translations.es[key] || key;

    // Reemplazar parámetros {0}, {1}, etc.
    if (params.length > 0) {
        params.forEach((param, index) => {
            text = text.replace(`{${index}}`, param);
        });
    }

    return text;
}

/**
 * Cambia el idioma actual
 * @param language - Nuevo idioma
 */
export function setLanguage(language: Language): void {
    if (translations[language]) {
        currentLanguage = language;
        localStorage.setItem('vysia-language', language);
        // Disparar un evento para que los componentes se actualicen
        window.dispatchEvent(new CustomEvent('language-changed', { detail: language }));
    }
}

/**
 * Obtiene el idioma actual
 * @returns Idioma actual
 */
export function getLanguage(): Language {
    return currentLanguage;
}

/**
 * Obtiene los idiomas disponibles
 * @returns Lista de idiomas disponibles
 */
export function getAvailableLanguages(): Language[] {
    return ['en', 'es', 'fr'];
}

/**
 * Hook para React que permite usar las traducciones y actualizarse cuando cambia el idioma
 * @param key - Clave de traducción
 * @param params - Parámetros opcionales
 * @returns Texto traducido
 */
export function useTranslation() {
    const [, forceUpdate] = React.useState({});

    React.useEffect(() => {
        const handleLanguageChange = () => forceUpdate({});
        window.addEventListener('language-changed', handleLanguageChange);
        return () => window.removeEventListener('language-changed', handleLanguageChange);
    }, []);

    return {
        t,
        language: currentLanguage,
        setLanguage,
        getAvailableLanguages
    };
}